import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionResources } from './hooks/useResources';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { ResourceType, ResourceAssignmentStatus } from './types/construction-resources.types';

const statusColors: Record<ResourceAssignmentStatus, string> = {
  planned: 'bg-slate-100 text-slate-600',
  reserved: 'bg-amber-100 text-amber-600',
  assigned: 'bg-blue-100 text-blue-600',
  in_use: 'bg-emerald-100 text-emerald-600',
  returned: 'bg-slate-900 text-white',
  cancelled: 'bg-red-100 text-red-600',
};

const statusLabels: Record<ResourceAssignmentStatus, string> = {
  planned: 'Lên kế hoạch',
  reserved: 'Đã giữ chỗ',
  assigned: 'Đã phân bổ',
  in_use: 'Đang sử dụng',
  returned: 'Đã hoàn trả',
  cancelled: 'Đã hủy',
};

const typeLabels: Record<ResourceType, string> = {
  equipment: 'Thiết bị máy móc',
  inventory: 'Vật tư thi công',
  utility: 'Tiện ích (Utility)',
  tool: 'Dụng cụ cầm tay',
  vehicle: 'Phương tiện vận chuyển',
  subcontractor: 'Nhà thầu phụ',
};

export default function ResourceListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    assignments,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionResources(companyId);

  return (
    <PageLayout
      title="Điều phối Tài nguyên (Resource Coordination)"
      description="Quản lý và điều phối thiết bị, vật tư và nhân lực cho các tổ đội thi công hiện trường."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Tài nguyên' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-calendar-event-line" onClick={() => navigate('./planning')}>Bảng điều phối</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./assign')}
          >
            Phân bổ tài nguyên
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Thiết bị đang dùng"
            value={stats?.activeEquipmentCount || 0}
            icon="ri-tools-line"
            variant="primary"
          />
          <KPICard
            title="Vật tư đã giữ"
            value={stats?.reservedMaterialCount || 0}
            icon="ri-box-3-line"
            variant="success"
          />
          <KPICard
            title="Báo cáo ngày chờ duyệt"
            value={stats?.pendingReports || 0}
            icon="ri-file-list-3-line"
            variant="warning"
          />
          <KPICard
            title="Xung đột tài nguyên"
            value={stats?.conflictCount || 0}
            icon="ri-error-warning-line"
            variant="danger"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm tên tài nguyên, thiết bị..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại tài nguyên', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'type', title: 'Loại' },
              { key: 'name', title: 'Tài nguyên / Thiết bị' },
              { key: 'team', title: 'Đội phụ trách' },
              { key: 'location', title: 'Định vị' },
              { key: 'dates', title: 'Thời gian gán' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={assignments}
            isLoading={isLoading}
            renderCell={(item, key) => {
              switch (key) {
                case 'type':
                  return <Badge variant="neutral" className="bg-slate-100 text-slate-500 text-[9px]  font-bold">{typeLabels[item.resource_type]}</Badge>;
                case 'name':
                  return <span className="font-bold text-slate-900">{item.resource_name}</span>;
                case 'team':
                  return (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded bg-os-primary/10 text-os-primary flex items-center justify-center text-[10px] font-bold ">
                        {item.team?.name?.charAt(0)}
                      </div>
                      <span className="text-xs font-bold text-slate-700 underline">{item.team?.name || '---'}</span>
                    </div>
                  );
                case 'location':
                  return (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{item.project?.name}</span>
                      <span className="text-[10px] text-slate-400 font-medium  italic">{item.zone?.name || 'Toàn dự án'}</span>
                    </div>
                  );
                case 'dates':
                  return (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{new Date(item.assigned_at).toLocaleDateString('vi-VN')}</span>
                      <span className="text-[9px] text-slate-400 italic">đến {item.returned_at ? new Date(item.returned_at).toLocaleDateString('vi-VN') : '---'}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
